import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import {
  MsalAuthenticationTemplate,
  UnauthenticatedTemplate,
  AuthenticatedTemplate,
} from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { loginRequest } from './config/msalConfig'
import AppShell from './components/layout/AppShell'
import ErrorBoundary from './components/ui/ErrorBoundary'
// Page components are lazy-loaded so each route (and its heavy deps like recharts)
// ships in its own chunk, keeping the initial bundle small. Auth-gate screens
// (LoginPage, AccessDenied) stay eager — they're on the critical first-paint path.
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Tenants = lazy(() => import('./pages/Tenants'))
const TenantDetail = lazy(() => import('./pages/TenantDetail'))
const MFAAudit = lazy(() => import('./pages/MFAAudit'))
const SecureScore = lazy(() => import('./pages/SecureScore'))
const RiskyUsers = lazy(() => import('./pages/RiskyUsers'))
const ConditionalAccess = lazy(() => import('./pages/ConditionalAccess'))
const AppCredentials = lazy(() => import('./pages/AppCredentials'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const Assessments = lazy(() => import('./pages/Assessments'))
const AssessmentReport = lazy(() => import('./pages/AssessmentReport'))
const FleetThreats = lazy(() => import('./pages/FleetThreats'))
const Threats = lazy(() => import('./pages/Threats'))
const Devices = lazy(() => import('./pages/Devices'))
const Compliance = lazy(() => import('./pages/Compliance'))
const Entitlements = lazy(() => import('./pages/Entitlements'))
const Organizations = lazy(() => import('./pages/Organizations'))
const AccessManagement = lazy(() => import('./pages/AccessManagement'))
const SignUp = lazy(() => import('./pages/SignUp'))
const Identity = lazy(() => import('./pages/Identity'))
const Discover = lazy(() => import('./pages/Discover'))
const Licensing = lazy(() => import('./pages/Licensing'))
const PrivilegedAccess = lazy(() => import('./pages/PrivilegedAccess'))
const ChangeMonitor = lazy(() => import('./pages/ChangeMonitor'))
const ChangeLog = lazy(() => import('./pages/ChangeLog'))
const ChangeAlertSettings = lazy(() => import('./pages/ChangeAlertSettings'))
import LoginPage from './pages/LoginPage'
import AccessDenied from './pages/AccessDenied'
import { useMe } from './hooks/useMe'
import { useApplyTheme } from './store/themeStore'

// Fallback while a lazy route chunk loads.
function RouteFallback() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

// Gate the portal on the current principal. A 403 (not_authorized) from the
// backend login gate renders the Access Denied screen instead of the app.
function PortalGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, isError, error } = useMe()
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  const resp = (error as { response?: { status?: number; data?: { code?: string } } })?.response
  if (isError && resp?.status === 403 && resp?.data?.code === 'not_authorized') {
    return <AccessDenied />
  }
  // A customer who authenticated from an enrolled tenant but holds no portal role
  // (not GA/Security Admin/Security Reader) gets no access — show the denied screen
  // rather than an empty app.
  if (me && me.userType === 'customer' && (me.roles?.length ?? 0) === 0) {
    return <AccessDenied />
  }
  return <>{children}</>
}

// MSP-only routes: a customer-portal user who reaches one by direct URL is sent to
// their dashboard (the backend also enforces this — this is the UX guard). The nav
// already hides these for customers.
function RequireInternal({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe()
  // Fail closed: render only for a positively internal user. `me` undefined (loading, or
  // /api/auth/me failed) used to fall through and render the MSP page.
  if (!me) return null
  if (me.userType !== 'internal') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Customer Global Admin only. UX guard: the backend's requireRole('CustomerGlobalAdmin')
// is what enforces it.
function RequireGlobalAdmin({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe()
  if (!me) return null
  if (!me.roles.includes('CustomerGlobalAdmin')) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function ProtectedLayout() {
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      errorComponent={({ error }) => (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="card p-6 max-w-md text-center">
            <p className="text-danger font-semibold mb-2">Authentication Error</p>
            <p className="text-text-muted text-sm">{error?.message ?? 'An unknown error occurred'}</p>
          </div>
        </div>
      )}
      loadingComponent={() => (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-text-muted text-sm">Authenticating...</p>
          </div>
        </div>
      )}
    >
      <PortalGate>
      <AppShell>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Identity */}
            <Route path="/identity" element={<Identity />} />
            <Route path="/mfa" element={<MFAAudit />} />
            <Route path="/risky-users" element={<RiskyUsers />} />
            {/* Conditional Access: deliberately UNGATED, matching the backend router
                (conditionalAccess.ts) which carries no requireFeature - Identity is
                baseline, as /mfa and /risky-users are. Tenant isolation comes from
                requireTenantAccess + scopeTenantQuery, not from hiding the route. */}
            <Route path="/conditional-access" element={<ConditionalAccess />} />
            {/* App Credentials: UNGATED like the rest of Identity, matching the backend
                router (appCredentials.ts), which carries no requireFeature. */}
            <Route path="/app-credentials" element={<AppCredentials />} />
            {/* Privileged Access: customer-visible when the org has the 'privileged' module
                (nav feature-gated). Backend gates /api/pim via requireFeature + tenant scope,
                and the page hides the MSP register tab for customers. */}
            <Route path="/privileged" element={<PrivilegedAccess />} />
            {/* Threats */}
            <Route path="/threats" element={<Threats />} />
            {/* E12 Devices — gated by the `devices` plan feature (nav hides it without) */}
            <Route path="/devices" element={<Devices />} />
            {/* E13 Compliance & DLP — gated by the `compliance` plan feature. The route
                itself is not a security boundary; requireFeature on the API mount is. */}
            <Route path="/compliance" element={<Compliance />} />
            {/* Legacy Defender Alerts URL → unified Threats hub */}
            <Route path="/threats/defender" element={<Navigate to="/threats" replace />} />
            {/* Posture */}
            <Route path="/secure-score" element={<SecureScore />} />
            <Route path="/assessments" element={<Assessments />} />
            <Route path="/assessments/baselines" element={<Assessments />} />
            <Route path="/assessments/run/:runId" element={<AssessmentReport />} />
            {/* Discover is customer-visible when the org has the 'discover' module (nav is
                feature-gated); the backend still gates data via requireFeature + tenant scope. */}
            <Route path="/discover" element={<Discover />} />
            {/* Licensing is customer-visible when the org has the 'licensing' module (nav is
                feature-gated); the backend still gates data via requireFeature + tenant scope. */}
            <Route path="/licensing" element={<Licensing />} />
            <Route path="/threat-indicators" element={<RequireInternal><FleetThreats /></RequireInternal>} />
            {/* Change Monitor — customer-visible when the org has change_monitor
                (nav is feature-gated); the backend still gates data via requireFeature + tenant scope. */}
            <Route path="/changes" element={<ChangeMonitor />} />
            <Route path="/changes/log" element={<ChangeLog />} />
            {/* Any internal (AlphaBOLD) user may view and manage alert settings; customers are
                redirected, and /api/changes/alerts/subscriptions admits no Customer* role. */}
            <Route path="/changes/alerts" element={<RequireInternal><ChangeAlertSettings /></RequireInternal>} />
            <Route path="/changes/detection" element={<Navigate to="/changes/log" replace />} />
            {/* Tenants — MSP-only */}
            <Route path="/tenants" element={<RequireInternal><Tenants /></RequireInternal>} />
            <Route path="/tenants/:id" element={<RequireInternal><TenantDetail /></RequireInternal>} />
            <Route path="/onboarding" element={<RequireInternal><Onboarding /></RequireInternal>} />
            <Route path="/entitlements" element={<RequireInternal><Entitlements /></RequireInternal>} />
            <Route path="/organizations" element={<RequireInternal><Organizations /></RequireInternal>} />
            <Route path="/access" element={<RequireGlobalAdmin><AccessManagement /></RequireGlobalAdmin>} />
            {/* Legacy route redirects → new locations */}
            <Route path="/findings" element={<Navigate to="/assessments" replace />} />
            <Route path="/threats/findings" element={<Navigate to="/assessments" replace />} />
            <Route path="/baselines" element={<Navigate to="/assessments/baselines" replace />} />
            <Route path="/threats/detections" element={<Navigate to="/threat-indicators" replace />} />
            <Route path="/fleet-threats" element={<Navigate to="/threat-indicators" replace />} />
            <Route path="/threats/changes" element={<Navigate to="/changes/log" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      </PortalGate>
    </MsalAuthenticationTemplate>
  )
}

export default function App() {
  useApplyTheme()
  const { pathname } = useLocation()
  // Public self-service signup — rendered OUTSIDE the MSAL auth gate (a prospective
  // customer has no account yet). It only calls public onboarding endpoints.
  if (pathname === '/signup') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <SignUp />
      </Suspense>
    )
  }
  return (
    <>
      <AuthenticatedTemplate>
        <ProtectedLayout />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  )
}
